import { Fragment, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { BookOpen, Bot } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HeaderButton } from '@/components/header-button';
import { docsUrl } from '@/lib/docs-url';
import { HeaderBell } from '@/components/header-bell';
import { ProfileMenu } from '@/components/profile-menu';
import { SupportMenu } from '@/components/support-menu';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useSidebarOrgUsageQuery } from '@/components/billing/queries';
import { useWorkspace } from '@/contexts/workspace-context';
import { isValidPublicId } from '@/lib/public-id';
import { orgUrl } from '@/lib/org-url';
import { BootstrapCodeDialog } from '@/pages/sandbox/bootstrap-code-dialog';

/**
 * Base app header — a fixed `h-12 border-b` bar shared by every signed-in
 * surface. Layout is fixed; content is injected via `actions` (right-aligned)
 * and an optional `left` slot (e.g. a super-admin title). There is no default
 * content — each surface passes its own `actions` (workspace/org use
 * `defaultHeaderActions()`).
 */
export function AppHeader({
  left,
  actions,
}: {
  left?: ReactNode;
  actions: ReactNode[];
}) {
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger className="md:hidden" />
      {left ? <div className="flex min-w-0 items-center gap-3">{left}</div> : null}
      <div className="flex flex-1 items-center justify-end gap-2">
        {actions.map((action, i) => (
          <Fragment key={i}>{action}</Fragment>
        ))}
      </div>
    </header>
  );
}

/**
 * AIT-393 — header entry to the agent bootstrap snippet, the same dialog the
 * dashboard card and Quick Start open. Minting needs an `X-Workspace-Id`
 * (backend `WorkspaceGuard`), which `useFetch` only attaches when a workspace
 * is active, and the workspace the user can actually see is the `:ws` segment
 * of the URL — the cached `activeWorkspace` can lag it or land on a different
 * workspace entirely (AIT-207). So the action lives on workspace routes only
 * and mints against the route param, never the cached context: on an org route
 * there is no visible target at all, and on `/w/:ws` the credentials must match
 * the workspace on screen.
 *
 * Membership, not just id shape, is the gate. This header renders in
 * `DashboardLayout`, one level ABOVE the `WorkspaceLayout` that validates
 * `:ws` and redirects — so a foreign or deleted workspace id would otherwise
 * offer a mint that can only 403 during that window.
 */
function AgentSetupAction() {
  const { ws } = useParams();
  const { workspaces } = useWorkspace();
  if (!ws || !workspaces.some((w) => w.id === ws)) return null;
  return (
    <BootstrapCodeDialog
      // Remount per target: navigating to another workspace with the dialog
      // open must not leave the previous workspace's minted code on screen.
      key={ws}
      variant="dashboard"
      targetWorkspaceId={ws}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          aria-label="AI setup"
        >
          <Bot className="size-4" />
          <span className="hidden md:inline">AI setup</span>
        </Button>
      }
    />
  );
}

/**
 * The standard workspace/org header actions: AI setup, Docs, Support,
 * notification bell, profile menu. Requires SessionProvider + ProfileProvider
 * above the header.
 *
 * AIT-393 — the agent bootstrap snippet also lives here, not just in the
 * dismissible dashboard card, so it stays reachable from every signed-in page.
 */
/** AIT-420 — a standing "Free trial" chip so a trialing org always knows it.
 *  Keyed off `trialDaysLeft != null` (Task 8: the Stripe-native trial —
 *  status 'trialing' — not the old `unlimited` meter flag); links to Plans.
 *  Same viewed-org resolution as HeaderBell: the URL's org wins, else the
 *  active workspace's org. */
function TrialBadgeAction() {
  const { orgId: urlOrgId } = useParams<{ orgId?: string }>();
  const { activeWorkspace, orgUsage } = useWorkspace();
  const wsOrgId = activeWorkspace?.organizationPublicId ?? null;
  const viewedOrgId = urlOrgId && isValidPublicId(urlOrgId, 'org') ? urlOrgId : wsOrgId;
  const needsOwnFetch = viewedOrgId !== null && viewedOrgId !== wsOrgId;
  const ownUsageQuery = useSidebarOrgUsageQuery(needsOwnFetch ? viewedOrgId : null);
  const usage = needsOwnFetch ? (ownUsageQuery.data ?? null) : orgUsage;
  if (!viewedOrgId || usage?.trialDaysLeft == null) return null;
  return (
    <Link to={orgUrl(viewedOrgId, 'plans')} aria-label="Free trial, see plans">
      <Badge variant="secondary">Free trial</Badge>
    </Link>
  );
}

export function defaultHeaderActions(): ReactNode[] {
  return [
    <TrialBadgeAction key="trial-badge" />,
    <AgentSetupAction key="agent-setup" />,
    <HeaderButton
      key="docs"
      icon={<BookOpen className="size-4" />}
      label="Docs"
      onClick={() => window.open(docsUrl(), '_blank', 'noopener,noreferrer')}
    />,
    <SupportMenu key="support" />,
    <HeaderBell key="bell" />,
    <ProfileMenu key="profile" />,
  ];
}
